/**
 * 权限配置 - Single Source of Truth
 *
 * 这是整个权限系统的统一导出入口，所有权限相关的配置都从这里获取：
 * - 资源权限定义 (PERMISSIONS_CONFIG)
 * - 角色权限矩阵 (ROLE_PERMISSIONS)
 * - 权限常量 (P) - 从 PERMISSIONS_CONFIG 自动生成
 * - 导航配置生成器 (getMainNavigation, getManagementNavigation, getNotificationNavigation)
 *
 * 代码即 SSOT：权限从配置直接派生，无需 DB 环路
 */

// ============================================
// 核心配置导出
// ============================================

export { PERMISSIONS_CONFIG } from "./resources";
export { ROLE_PERMISSIONS } from "./roles";

// ============================================
// 权限常量导出（从 PERMISSIONS_CONFIG 自动生成）
// ============================================

export * from "./constants";

// ============================================
// 类型定义导出
// ============================================

export type {
  ScopeStrategy,
  NavigationGroup,
  NavigationRawConfig,
  PermissionDefinition,
  PermissionsConfigMap,
  ResourceConfig,
  RoleId,
  RolePermissionsMap,
} from "./types";

// ============================================
// 导航配置导出
// ============================================

export {
  getMainNavigation,
  getManagementNavigation,
  getNavigationLabel,
  getNavigationPath,
  getNotificationNavigation,
  isNavigationVisible,
  type NavigationConfig,
  projectNavigationConfig,
} from "./navigation";

// ============================================
// 派生数据
// ============================================

import { PERMISSIONS_CONFIG } from "./resources";

/**
 * 资源显示名称映射
 *
 * ⚠️ 这是从 PERMISSIONS_CONFIG 自动生成的，不要直接修改
 * ⚠️ 请修改 resources/*.config.ts 中的 displayName 字段
 */
export const RESOURCE_DISPLAY_NAMES = Object.fromEntries(
  Object.entries(PERMISSIONS_CONFIG).map(([key, config]) => [key, config.displayName]),
) as Record<keyof typeof PERMISSIONS_CONFIG, string>;

/**
 * 获取资源显示名称
 */
export function getResourceDisplayName(resourceType: keyof typeof PERMISSIONS_CONFIG): string {
  return RESOURCE_DISPLAY_NAMES[resourceType] || resourceType;
}

// ============================================
// 类型推导（从配置自动生成）
// ============================================

import type { ROLE_PERMISSIONS } from "./roles";

/**
 * 资源类型 - 从配置自动推导
 */
export type ResourceType = keyof typeof PERMISSIONS_CONFIG;

/**
 * 权限类型 - 从配置自动推导
 *
 * 格式: "resource:action:scope" | "resource:action"
 * 例如: "student:read:all", "knowledge-entry:review:all"
 *
 * ⚠️ 这是从 PERMISSIONS_CONFIG 自动生成的，不要手动修改
 */
export type Permission = {
  [Resource in keyof typeof PERMISSIONS_CONFIG]: keyof (typeof PERMISSIONS_CONFIG)[Resource]["permissions"] extends string
    ? `${Resource & string}:${keyof (typeof PERMISSIONS_CONFIG)[Resource]["permissions"] & string}`
    : never;
}[keyof typeof PERMISSIONS_CONFIG];

/**
 * 角色类型
 */
export type Role = keyof typeof ROLE_PERMISSIONS;
